import { slugify } from '@/lib/utils'

export interface TocItem {
    id: string
    text: string
}

// Article bodies are either Markdown (older posts) or HTML (posts saved from
// the WYSIWYG editor). HTML content begins with a tag.
export function isHtmlContent(content: string): boolean {
    return /^\s*</.test(content)
}

export function extractToc(markdown: string): TocItem[] {
    const lines = markdown.split('\n')
    const items: TocItem[] = []
    let inCodeBlock = false

    for (const line of lines) {
        if (line.trim().startsWith('```')) {
            inCodeBlock = !inCodeBlock
            continue
        }

        if (inCodeBlock) continue

        const match = /^##\s+(.+?)\s*$/.exec(line)

        if (match) {
            const text = match[1].replace(/[*_`]/g, '').trim()
            items.push({ id: slugify(text), text })
        }
    }

    return items
}

export function extractTocFromHtml(html: string): TocItem[] {
    const items: TocItem[] = []
    const regex = /<h2\b[^>]*>([\s\S]*?)<\/h2>/gi
    let match: RegExpExecArray | null

    while ((match = regex.exec(html)) !== null) {
        const text = match[1].replace(/<[^>]+>/g, '').trim()

        if (text) items.push({ id: slugify(text), text })
    }

    return items
}

export function tocFor(content: string): TocItem[] {
    return isHtmlContent(content) ? extractTocFromHtml(content) : extractToc(content)
}

// Adds stable, slug-based ids to h2/h3 headings in HTML content so the table of
// contents can link to them.
export function injectHeadingIds(html: string): string {
    return html.replace(/<(h2|h3)\b([^>]*)>([\s\S]*?)<\/\1>/gi, (_full, tag, attrs, inner) => {
        const text = String(inner).replace(/<[^>]+>/g, '').trim()
        const id = slugify(text)
        const hasId = /\bid\s*=/.test(attrs)
        const idAttr = hasId ? '' : ` id="${id}"`

        return `<${tag}${attrs}${idAttr} class="scroll-mt-28">${inner}</${tag}>`
    })
}
